/**
 * Action: Service of summons (serve the summons and complaint and file the proof).
 * Statewide base with the court layer.
 *
 * Every rule cited here comes from the files in data/. Court specifics come from
 * src/courts/ through courtDetail(); anything the files do not cover is listed under
 * "verify" rather than asserted.
 */
import {
  addDays, checklist, fmt, fmtISO, iso, lastDayAfter, lastDayAfterYears, opts, parse, todayISO,
} from "../lib/civil.js";

export const id = "service-of-summons";
export const statewide = true;
// The statewide forms its checklist can link, which a case offers when this action is added to it.
export const forms = ["ca-sum-100", "ca-cm-010", "ca-cm-020", "ca-civ-050", "ca-civ-100", "ca-pos-010", "ca-pos-015"];
// What kind of case it is, kept once per case (caseFacts in src/cases.js).
export const caseFacts = {
  from: (f) => ({ ctype: f.collections === "yes" ? "collections" : f.collections === "no" ? "general" : undefined }),
  to: (a) => ({ collections: a.ctype ? (a.ctype === "collections" ? "yes" : "no") : undefined }),
};

const DEF_TXT = {
  person: "An individual adult",
  corp: "A corporation (including an LLC served as one)",
  assoc: "A partnership or other unincorporated association",
  public: "A public entity",
  minor: "A minor",
  ward: "An adult with a guardian or conservator",
};
const METHOD_TXT = {
  personal: "Personal delivery (CCP 415.10)",
  sub: "Substituted service: leave the papers, then mail a copy (CCP 415.20)",
  ack: "Mail with notice and acknowledgment of receipt (CCP 415.30)",
  mailOut: "First-class mail with return receipt, to a defendant outside California (CCP 415.40)",
  pub: "Publication, by court order (CCP 415.50)",
};
const yesNo = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const ENTITY = ["corp", "assoc", "public"];
// The 2027 version of CCP 415.20 and 417.10 applies to service on or after this date.
const NEW_RULES = "2027-01-01";

const methodsFor = (a) => {
  const m = ["personal", "sub", "ack"];
  if (a.where === "out") m.push("mailOut");
  m.push("pub");
  return m;
};
const dateLabel = {
  personal: "Date the papers were (or will be) delivered",
  sub: "Date the copy was (or will be) mailed after the papers were left",
  ack: "Date the notice and acknowledgment was (or will be) mailed",
  mailOut: "Date the papers were (or will be) mailed",
  pub: "Date of the first publication",
};

/* ------------------------------------------------------------------ steps */
export const steps = [
  {
    id: "case",
    title: "The case and the service deadline",
    intro: "The deadline to serve and file proof of service runs from the filing of the complaint. The wizard doesn't ask for names or case details.",
    fields: [
      {
        k: "ctype", type: "radio", label: "What kind of case is it?", req: true,
        opts: [
          { v: "general", l: "A general civil case" },
          { v: "collections", l: "A collections case under CRC 3.740", h: "$35,000 or less owed on credit, with no tort or punitive damages, property recovery or prejudgment attachment sought (CRC 3.740(a))." },
        ],
      },
      {
        k: "added", type: "radio", label: "Was this defendant added by an amended complaint?", req: true,
        show: (a) => a.ctype === "general",
        opts: yesNo,
      },
      {
        k: "fileDate", type: "date", req: true,
        label: (a) => (a.added === "yes" ? "Date the amended complaint was filed" : "Date the complaint was filed"),
      },
    ],
  },
  {
    id: "defendant",
    title: "The defendant",
    intro: "Who receives the papers, and which methods are allowed, depend on the kind of defendant.",
    fields: [
      { k: "dtype", type: "radio", label: "Who is being served?", req: true, opts: opts(DEF_TXT) },
      {
        k: "where", type: "radio", label: "Where will the defendant be served?", req: true,
        opts: [{ v: "in", l: "In California" }, { v: "out", l: "Outside California" }],
      },
      { k: "doe", type: "radio", label: "Is the defendant being served under a fictitious (Doe) name?", req: true, opts: yesNo },
      { k: "pi", type: "radio", label: "Is it an action for personal injury or wrongful death?", req: true, opts: yesNo },
    ],
  },
  {
    id: "method",
    title: "Manner of service",
    intro: "Service is complete at a different time for each method, and each needs its own proof.",
    fields: [
      {
        k: "method", type: "radio", label: "How will the summons be served?", req: true,
        opts: (a) => methodsFor(a).map((v) => ({ v, l: METHOD_TXT[v] })),
        hint: (a) => (ENTITY.includes(a.dtype)
          ? "Substituted service on an entity is made at the office of the person to be served (CCP 415.20(a))."
          : "Substituted service on an individual is allowed only after personal delivery can't be made with reasonable diligence (CCP 415.20(b))."),
      },
      {
        k: "serveDate", type: "date",
        label: (a) => `${dateLabel[a.method] || "Date of service"} (optional)`,
        hint: "Used to work out when service is complete and when the response is due.",
      },
    ],
  },
];

/** Shown on the review step. */
export function summary(a, court) {
  const rows = [];
  const add = (l, v) => v && rows.push([l, v]);
  add("Court", court.name);
  add("Case", a.ctype === "collections" ? "Collections case (CRC 3.740)" : a.ctype ? "General civil case" : "");
  if (a.added) add("Added by amendment", a.added === "yes" ? "Yes" : "No");
  add(a.added === "yes" ? "Amended complaint filed" : "Complaint filed", fmtISO(a.fileDate));
  add("Defendant", DEF_TXT[a.dtype]);
  add("Served", a.where === "out" ? "Outside California" : a.where ? "In California" : "");
  add("Doe defendant", a.doe === "yes" ? "Yes" : a.doe === "no" ? "No" : "");
  add("Personal injury or wrongful death", a.pi === "yes" ? "Yes" : a.pi === "no" ? "No" : "");
  add("Method", METHOD_TXT[a.method]);
  add(dateLabel[a.method] || "Date", fmtISO(a.serveDate));
  return rows;
}

/* ------------------------------------------------------------------ helpers */
// When service is complete, from the date the wizard asked for.
function completion(a) {
  if (!a.serveDate) return null;
  const d = parse(a.serveDate);
  switch (a.method) {
    case "personal": return { date: d, cite: "CCP 415.10", how: "on delivery" };
    case "sub": return { date: addDays(d, 10), cite: "CCP 415.20", how: "on the 10th day after mailing" };
    case "mailOut": return { date: addDays(d, 10), cite: "CCP 415.40", how: "on the 10th day after mailing" };
    // GC 6064: four weekly publications; the period ends at the end of the 28th day, counting the first.
    case "pub": return { date: addDays(d, 27), cite: "CCP 415.50(c); GC 6064", how: "at the end of the 28th day, counting the day of first publication" };
    default: return null; // CCP 415.30: on the date the acknowledgment is signed, if it is returned
  }
}

function whoGetsIt(a) {
  switch (a.dtype) {
    case "corp": return ["Deliver to the corporation's designated agent for service of process; or to its president, CEO or other head, a vice president, secretary or assistant secretary, treasurer or assistant treasurer, controller or CFO, general manager, or a person it has authorized to receive service (for a bank, also a cashier or assistant cashier).", "CCP 416.10(a)-(c)"];
    case "assoc": return ["For a general or limited partnership, deliver to the agent for service of process named in its Secretary of State statement, a general partner or the general manager. For another unincorporated association, to its agent for service, president or other head, a vice president, secretary or assistant secretary, treasurer or assistant treasurer, general manager, or a person it has authorized.", "CCP 416.40(a)-(b)"];
    case "public": return ["Deliver to the clerk, secretary, president, presiding officer or other head of the public entity's governing body.", "CCP 416.50"];
    case "minor": return ["Deliver to the minor's parent, guardian, conservator or similar fiduciary (or, if none can be found with reasonable diligence, a person with care or control of the minor, or with whom the minor lives or works), and also to the minor if 12 or older.", "CCP 416.60"];
    case "ward": return ["Deliver to the guardian, conservator or similar fiduciary and to the person, unless the court excuses delivery to the person for good cause.", "CCP 416.70"];
    default: return ["Deliver to the defendant or to a person the defendant has authorized to receive service of process.", "CCP 416.90"];
  }
}

/* ------------------------------------------------------------------ checklist */
export function build(a, court) {
  const c = checklist();
  const collections = a.ctype === "collections";
  const fileISO = a.fileDate || todayISO();
  const days = collections ? 180 : a.added === "yes" ? 30 : 60;
  const proofBy = lastDayAfter(court, fileISO, days);
  const outer = lastDayAfterYears(court, fileISO, 3);
  const done = completion(a);
  const respond = done ? lastDayAfter(court, iso(done.date), 30) : null;
  const newRules = (a.serveDate || todayISO()) >= NEW_RULES;
  const entity = ENTITY.includes(a.dtype);

  /* ---- papers ---- */
  const p1 = c.section("papers", "Phase 1. Put together what is served");
  c.item(p1, "sc", "A copy of the issued summons (SUM-100) and of the complaint.", "CCP 415.10; 412.20", "", ["ca-sum-100"]);
  if (a.doe === "yes") c.item(p1, "doe", "Complete the notice on the face of the summons copy saying the defendant is served as the person sued under the fictitious name. Without it, no default can be entered against a Doe defendant.", "CCP 474");
  c.court(p1, "adr", court, "rules.adrPackage", "ADR information package and where to get it",
    { text: "The court's ADR information package. In a general civil case the plaintiff serves it on each defendant with the complaint.", cite: "CRC 3.221(c)" });
  if (collections) c.item(p1, "cm010", "A copy of the Civil Case Cover Sheet (CM-010) marked as a collections case.", "CRC 3.220(a); 3.740(b)", "", ["ca-cm-010"]);
  else c.item(p1, "cm010", "If the cover sheet designated the case as complex, a copy of the Civil Case Cover Sheet (CM-010). Otherwise it isn't served.", "CRC 3.220(a); 3.401", "", ["ca-cm-010"]);
  c.court(p1, "cmc", court, "rules.cmcNotice", "local rules on serving the notice of the case management conference", null);
  if (a.pi === "yes") c.item(p1, "civ050", "A Statement of Damages (CIV-050). It must be served before a default can be taken, and on a defendant who hasn't appeared it is served the same way as a summons, so serving it now saves a second service.", "CCP 425.11(c)-(d)", "", ["ca-civ-050"]);
  if (a.method === "ack") c.item(p1, "pos015", "Two copies of the Notice and Acknowledgment of Receipt (POS-015) and a postage-paid return envelope addressed to the sender.", "CCP 415.30(a), (e)", "", ["ca-pos-015"]);

  /* ---- who ---- */
  const p2 = c.section("who", "Phase 2. Who serves it and who receives it");
  c.item(p2, "server", "Service must be made by someone at least 18 who is not a party to the action.", "CCP 414.10");
  const [whoText, whoCite] = whoGetsIt(a);
  c.item(p2, "to", whoText, whoCite);
  if (a.dtype === "corp") c.item(p2, "sos", "If the corporation can't be served this way, service through the Secretary of State is possible only where the Corporations Code authorizes it.", "CCP 416.10(d)", "The Corporations Code is not in the downloaded files.");

  /* ---- how ---- */
  const p3 = c.section("how", "Phase 3. Serve it");
  if (a.method === "personal") {
    c.item(p3, "deliver", "Deliver a copy of the summons and complaint to the person to be served. Service is complete on delivery.", "CCP 415.10");
    c.item(p3, "date", "Write the date of delivery on the face of the summons copy at the time it is delivered (service without it is still valid).", "CCP 415.10");
  }
  if (a.method === "sub") {
    if (entity) {
      c.item(p3, "leave", "Leave a copy of the summons and complaint during usual office hours at the office of the person to be served, with the person apparently in charge. If no physical address is known, leave it at their usual mailing address (not a USPS post office box) with a person at least 18, who must be told what the papers are.", "CCP 415.20(a)");
    } else {
      c.item(p3, "diligence", newRules
        ? "First try personal delivery with reasonable diligence: in good faith, on at least three occasions, on three different days, at three different times. In an action to collect a consumer debt, at least one attempt must be at the defendant's home if it is known or can be found with reasonable effort."
        : "First try to deliver the papers personally with reasonable diligence. Substituted service on an individual is allowed only when that fails.", newRules ? "CCP 415.20(b)(2)-(3) (operative January 1, 2027)" : "CCP 415.20(b)");
      c.item(p3, "leave", "Leave a copy of the summons and complaint at the person's home, usual place of abode, usual place of business or usual mailing address (not a USPS post office box), with a competent member of the household or a person apparently in charge, at least 18, who must be told what the papers are.", "CCP 415.20(b)");
      c.item(p3, "cmra", "If the only known address is a private mailbox at a commercial mail receiving agency, the papers can be left with the agency on the first attempt.", "CCP 415.20(c)");
    }
    c.item(p3, "mail", newRules
      ? "Then mail a copy of the summons and complaint, postage prepaid, to the place where the papers were left, by USPS first-class mail, Priority Mail with tracking, or Certified Mail with return receipt requested."
      : "Then mail a copy of the summons and complaint by first-class mail, postage prepaid, to the place where the papers were left.", newRules ? "CCP 415.20 (operative January 1, 2027)" : "CCP 415.20");
    c.item(p3, "complete", "Service is complete on the 10th day after the mailing.", "CCP 415.20");
  }
  if (a.method === "ack") {
    c.item(p3, "mail", "Mail the summons, complaint, two copies of POS-015 and the return envelope by first-class mail or airmail, postage prepaid.", "CCP 415.30(a)", "", ["ca-pos-015"]);
    c.item(p3, "complete", "Service is complete on the date the acknowledgment is signed, if it is then returned. For an entity, it must be signed in the entity's name by a person authorized to receive service for it.", "CCP 415.30(b)-(c)");
    c.item(p3, "noreturn", "If the acknowledgment isn't returned within 20 days of mailing, serve by another method. The court, on motion, awards the reasonable expenses of that service against the party who didn't return it.", "CCP 415.30(d)");
  }
  if (a.method === "mailOut") {
    c.item(p3, "mail", "Send a copy of the summons and complaint by first-class mail, postage prepaid, requiring a return receipt. Service is complete on the 10th day after mailing.", "CCP 415.40");
    c.item(p3, "receipt", "Keep the signed return receipt or other evidence of actual delivery: the proof of service must include it.", "CCP 417.20(a)");
  }
  if (a.method === "pub") {
    c.item(p3, "order", "Apply for an order for publication, with an affidavit showing the defendant can't be served with reasonable diligence in another manner and that a cause of action exists against them (or they claim an interest in property in California).", "CCP 415.50(a), (e)");
    c.court(p3, "where", court, "rules.serviceOrders", "local rules on which department hears applications for service by publication", null);
    c.item(p3, "fee", "Fee for the request for an order for publication: $20.", "GC 70617(c)(3); Fee Schedule item 59");
    c.item(p3, "publish", "Publish the summons in the newspaper the order names, once a week for four successive weeks (four publications with at least five days between them). If the defendant's address becomes known before publication ends, mail them the summons, complaint and order.", "CCP 415.50(b); GC 6064");
    c.item(p3, "complete", "Service is complete at the end of the 28th day, counting the day of first publication.", "CCP 415.50(c); GC 6064");
    if (collections) c.item(p3, "coll", "In a collections case, obtaining the order for publication within 180 days after filing also meets the service deadline.", "CRC 3.740(d)");
  }
  if (a.where === "out" && a.method !== "mailOut") c.item(p3, "outstate", "A defendant outside California can be served by any manner in this article; proof is made as for service in California, or as the law of the place of service or a court order provides.", "CCP 415.40; 417.20");

  /* ---- proof ---- */
  const p4 = c.section("proof", "Phase 4. Prove service and file the proof");
  const proofForm = ["ca-pos-010"];
  let proofText = "Complete a Proof of Service of Summons (POS-010), signed by the person who served it, showing the time, place and manner of service and the name (and, for an entity, the title or capacity) of the person who received the papers.";
  let proofCite = "CCP 417.10(a), (f)";
  if (a.method === "ack") { proofText += " Attach the signed acknowledgment of receipt (POS-015)."; proofCite = "CCP 417.10(a)"; proofForm.push("ca-pos-015"); }
  if (a.method === "mailOut") { proofText += " Attach the signed return receipt or other evidence of actual delivery."; proofCite = "CCP 417.20(a)"; }
  if (a.method === "pub") { proofText = "Proof is the publisher's affidavit showing the time and place of publication, and an affidavit showing when and where the summons and complaint were mailed to the defendant, if they were. Attach them to a POS-010."; proofCite = "CCP 417.10(b)"; }
  c.item(p4, "pos", proofText, proofCite, "", proofForm);
  if (a.doe === "yes") c.item(p4, "doepos", "The proof must state the fictitious name the defendant was served under and that the notice was given on the summons.", "CCP 474");
  if ((a.method === "personal" || a.method === "sub") && a.where === "in") {
    c.item(p4, "photos", newRules
      ? "Attach, for each attempted or completed service, one or more photographs of the site with a readable stamp recording the date, time and GPS (or equivalent) coordinates. At a home or business, at least one must show the door (or the entrance, explaining why the door wasn't accessible). A photograph can be left out if taking it would have compromised the server's safety, with a detailed statement explaining why."
      : "For service on or after January 1, 2027, the proof must include date-, time- and GPS-stamped photographs of the site of each attempted or completed service. Service before then doesn't need them.",
    "CCP 417.10(a)(1)-(5) (operative January 1, 2027)");
    if (newRules) c.verify("POS-010 in the downloaded forms is the January 2007 revision. Check whether the Judicial Council has revised it for the photograph requirement that applies from January 1, 2027.");
  }
  c.item(p4, "file", `File the proof of service by ${fmt(proofBy.date)}, ${collections ? "180 days after the complaint was filed" : a.added === "yes" ? "30 days after the amended complaint was filed" : "60 days after the complaint was filed"}, unless the defendant has already made a general appearance.`, collections ? "CRC 3.740(d); CCP 417.30" : "CRC 3.110(b); CCP 417.30");
  c.court(p4, "efile", court, "efiling.represented", "local rules on whether represented parties must e-file",
    { text: "File the proof of service with the clerk.", cite: "CCP 417.30" });

  /* ---- deadlines ---- */
  const p5 = c.section("dead", "Phase 5. If service runs late");
  if (!collections) {
    c.item(p5, "extend", "Apply for an order extending time before the deadline passes, with a declaration explaining why service isn't complete, the efforts made, and the date it will be completed.", "CRC 3.110(e)", "", ["ca-cm-020"]);
    c.court(p5, "extendlocal", court, "rules.extensionToServe", "local rules on where an application to extend time to serve is filed and heard", null);
    c.item(p5, "osc", "Without service or an extension, the court may issue an order to show cause why sanctions shouldn't be imposed.", "CRC 3.110(f)");
  } else {
    c.item(p5, "osc", "If the proofs aren't filed (and no publication order obtained) within 180 days, the court may issue an order to show cause about sanctions. Filing them at least 10 court days before the hearing continues it to 360 days after filing.", "CRC 3.740(e)");
  }
  c.item(p5, "outer", `The summons and complaint must be served within three years after the action is commenced against the defendant (by ${fmt(outer.date)}), and proof filed within 60 days after that. Missing it means mandatory dismissal.`, "CCP 583.210; 583.250");

  /* ---- after ---- */
  if (done || a.method === "ack") {
    const p6 = c.section("after", "Phase 6. After service");
    if (done) c.item(p6, "respond", `Service is complete ${done.how} (${fmt(done.date)}). The defendant's response is due 30 days after that: ${fmt(respond.date)}.`, `${done.cite}; CCP 412.20(a)(3)`, respond.moved ? `The 30th day, ${fmt(respond.raw)}, isn't a court day, so the period runs to the next one (CCP 12a).` : "");
    else c.item(p6, "respond", "The defendant's response is due 30 days after service is complete, which is the date the acknowledgment is signed.", "CCP 415.30(c); 412.20(a)(3)");
    if (!collections) c.item(p6, "ext", "The parties may agree, without leave of court, to one 15-day extension of the time to respond.", "CRC 3.110(d)");
    if (!collections) c.item(p6, "default", "If no response is served in time and no extension is granted, file a request for entry of default within 10 days after the time for service of the response has elapsed.", "CRC 3.110(g)", "", ["ca-civ-100"]);
    else c.item(p6, "default", "If defendants don't file responsive pleadings, the plaintiff must obtain a default judgment within 360 days after the complaint was filed.", "CRC 3.740(f)", "", ["ca-civ-100"]);
  }

  /* ---- rows, verify ---- */
  c.row(collections ? "Serve and file proof by" : "File proof of service by", fmt(proofBy.date), `${days} days after ${fmtISO(fileISO)}${proofBy.moved ? `, moved from ${fmt(proofBy.raw)} to the next court day` : ""}`);
  if (done) {
    c.row("Service complete", fmt(done.date), done.cite);
    c.row("Response due", fmt(respond.date), "30 days after service is complete (CCP 412.20(a)(3))");
  }
  c.row("Three-year limit", fmt(outer.date), "CCP 583.210(a)");
  c.dates(court, parse(fileISO), respond && respond.date > proofBy.date ? respond.date : proofBy.date);
  if (a.where === "out") c.verify("Service in another country, and the Hague Service Convention, are not covered by the downloaded files.");
  if (a.dtype === "public") c.verify("Claim-presentation requirements before suing a public entity are not in the downloaded files.");
  c.verify("Registration requirements for process servers are not in the downloaded files.");

  const out = c.result();
  let n = 1;
  out.sections.forEach((s) => { s.title = s.title.replace(/^Phase \d+\./, `Phase ${n++}.`); });
  return out;
}
